import React, { useMemo, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";

interface Props {
  indicatorName: string;                 // Nom de la colonne y
  rows: Record<string, any>[];           // Les données de l’indicateur
  chartType: "bar" | "line" | "area" | "pie";  // Type de graphique
  xKey?: string;                         // Colonne utilisée pour l’axe X / catégorie
}

const COLORS = ["#1565C0", "#ff6f00", "#2e7d32", "#c62828", "#6a1b9a", "#00897b"];

const IndicatorChart: React.FC<Props> = ({ indicatorName, rows, chartType, xKey }) => {
  const [currentXKey, setCurrentXKey] = useState<string>(xKey??"");
  const chartData = useMemo(() => {
    if (!currentXKey) return [];

    const map = new Map<string, number>();
    rows.forEach((row) => {
      const x = row[currentXKey];
      const y = Number(row[indicatorName]) || 0;
      if (!x) return;
      map.set(x, (map.get(x) ?? 0) + y);
    });

    return Array.from(map.entries()).map(([x, y]) => ({ name: x, value: y }));
  }, [rows, currentXKey, indicatorName]);
  if (!rows || rows.length === 0) return <p style={{textAlign:"center"}}>Aucune donnée</p>;

  const xCandidates = Object.keys(rows[0]).filter(
    (k) =>
      k !== indicatorName
  );

  if(currentXKey===""){
    setCurrentXKey(xCandidates[0]);
  }

  return (
    <div style={{ marginTop: 20 }}>
      {!xKey && (
        <div style={{ marginBottom: 10 }}>
          <label>Référence :</label>{" "}
          <select className="form-select"
          style={{display:"inline-block",width:"15%"}}
          value={currentXKey}
          onChange={(e) => setCurrentXKey(e.target.value)}>
            {xCandidates.map((key) => (
              <option key={key} value={key}>{key}</option>
            ))}
          </select>
        </div>
      )}

      <div style={{width:"100%", height:300}}>
          {chartType === "bar" && (
            <ResponsiveContainer>
            <BarChart data={chartData}>
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#1565C0" />
            </BarChart>
            </ResponsiveContainer>
          )}
          {chartType === "line" && (
            <ResponsiveContainer>
            <LineChart data={chartData}>
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="value" stroke="#1565C0" strokeWidth={2} />
            </LineChart>
            </ResponsiveContainer>
          )}
          {chartType === "area" && (
            <ResponsiveContainer>
            <AreaChart data={chartData}>
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Area type="monotone" dataKey="value" stroke="#1565C0" fill="#1565C033" />
            </AreaChart>
            </ResponsiveContainer>
          )}
          {chartType === "pie" && (
            <ResponsiveContainer>
            <PieChart>
              <Pie
                data={chartData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={80}
                label
              >
                {chartData.map((entry, idx) => (
                  <Cell key={`cell-${idx}`} fill={COLORS[idx % COLORS.length]} />
                ))}
              </Pie>
              <Legend />
              <Tooltip />
            </PieChart>
            </ResponsiveContainer>
          )}
      </div>
    </div>
  );
};

export default IndicatorChart;